import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { FormTemplate } from '../types/formBuilder';
import { TemplateService } from '../services/templateService';
import CommonPageHeader from '../components/common/CommonPageHeader';
import FormBuilder from '../components/formBuilder/FormBuilder';
import { 
  RectangleStackIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const FormBuilderEditor: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { user } = useAuth();
  
  // State
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get template ID from URL params if editing existing template
  const templateId = searchParams.get('templateId');

  // Load template if editing
  React.useEffect(() => {
    if (templateId && user?.organizationId) {
      loadTemplate(templateId);
    }
  }, [templateId, user?.organizationId]);

  const loadTemplate = async (id: string) => {
    setIsLoading(true);
    try {
      const template = await TemplateService.getFormTemplate(id);
      setSelectedTemplate(template);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error(t('formBuilder.errors.loadFailed') || 'Failed to load template');
      navigate('/form-builder'); // Redirect back if template not found
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTemplate = async (template: FormTemplate) => {
    if (!user?.organizationId || !user?.uid) {
      toast.error('User not authenticated');
      return;
    }

    try {
      if (templateId) {
        // Update existing template
        const updatedTemplate = {
          ...template,
          updatedAt: new Date(),
          version: (template.version || 0) + 1
        };
        await TemplateService.updateFormTemplate(templateId, updatedTemplate);
        setSelectedTemplate(updatedTemplate); // Update local state
        toast.success(t('formBuilder.messages.updateSuccess') || 'Template updated successfully');
      } else {
        // Create new template
        const newTemplate = {
          ...template,
          organizationId: user.organizationId,
          createdBy: user.uid,
          createdByName: user.displayName || user.email || 'Unknown User',
          createdAt: new Date(),
          updatedAt: new Date(),
          version: 1
        };
        await TemplateService.createFormTemplate(newTemplate);
        setSelectedTemplate(newTemplate); // Update local state
        toast.success(t('formBuilder.messages.createSuccess') || 'Template created successfully');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(t('formBuilder.errors.saveFailed') || 'Failed to save template');
      throw error; // Re-throw to prevent navigation
    }
  };

  const handleCloseBuilder = () => {
    navigate('/form-builder');
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Header with controls */}
      <CommonPageHeader
        title="Form Builder"
        subtitle="Vizuális form szerkesztő"
        icon={RectangleStackIcon}
      >
        {/* Form controls */}
        <button
          onClick={handleCloseBuilder}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          {t('common.back') || 'Back'}
        </button>

        <div className="h-6 w-px bg-white/20"></div>

      </CommonPageHeader>

      {/* Form Builder Content */}
      <div className="flex-1 min-h-0">
        <FormBuilder
          isOpen={true}
          onClose={handleCloseBuilder}
          onSave={handleSaveTemplate}
          initialTemplate={selectedTemplate || undefined}
          useFullPage={true}
        />
      </div>
    </div>
  );
};

export default FormBuilderEditor;
